import React, { useEffect, useState } from "react";
import { Building2, Globe, Calendar, DollarSign, Users, Briefcase, Headphones, FileText, Plug } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import pt from "@/lib/i18n";

const StatTile = ({ Icon, label, value, idx }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: idx * 0.04 }}
        className="rounded-xl p-4 flex items-center gap-3"
        style={{
            background: "var(--bg-elevated)",
            border: "1px solid var(--glass-border)",
        }}
        data-testid={`stat-${label}`}
    >
        <div
            className="rounded-md p-2.5"
            style={{ background: "var(--copper-subtle)", color: "var(--copper-dark)" }}
        >
            <Icon size={18} strokeWidth={1.6} />
        </div>
        <div>
            <div className="font-display text-2xl text-[color:var(--slate-deep)] leading-none">
                {value ?? 0}
            </div>
            <div className="font-body text-[11px] uppercase tracking-[0.14em] text-[color:var(--slate-light)] mt-1">
                {label}
            </div>
        </div>
    </motion.div>
);

const TenantsPage = () => {
    const [orgs, setOrgs] = useState(null);

    useEffect(() => {
        apiGet("/admin/tenants").then(setOrgs).catch(() => setOrgs([]));
    }, []);

    if (!orgs) {
        return (
            <GlassCard padding="p-6">
                <Skeleton height="320px" />
            </GlassCard>
        );
    }

    return (
        <div data-testid="tenants-page">
            <PageHeader title={pt.admin.tenants.title} subtitle={pt.admin.tenants.subtitle} testId="tenants-header" />
            <div className="space-y-5">
            {orgs.map((org) => (
                <GlassCard key={org.id} padding="p-6" testId={`org-${org.slug}`}>
                    <div className="flex items-start gap-4 mb-6">
                        <div
                            className="rounded-xl p-4"
                            style={{ background: "var(--copper-subtle)", color: "var(--copper-dark)" }}
                        >
                            <Building2 size={28} strokeWidth={1.4} />
                        </div>
                        <div className="flex-1">
                            <h2 className="font-display text-3xl text-[color:var(--slate-deep)]">{org.name}</h2>
                            <p className="font-mono text-xs text-[color:var(--slate-light)] mt-1">{org.slug}</p>
                            <div className="flex flex-wrap items-center gap-4 mt-3 font-body text-xs text-[color:var(--slate-mid)]">
                                <div className="inline-flex items-center gap-1.5">
                                    <Globe size={13} className="text-[color:var(--copper)]" />
                                    {pt.admin.tenants.language}: <span className="font-medium">{org.primary_language?.toUpperCase()}</span>
                                </div>
                                <div className="inline-flex items-center gap-1.5">
                                    <Calendar size={13} className="text-[color:var(--copper)]" />
                                    {pt.admin.tenants.timezone}: <span className="font-medium">{org.timezone}</span>
                                </div>
                                <div className="inline-flex items-center gap-1.5">
                                    <DollarSign size={13} className="text-[color:var(--copper)]" />
                                    {pt.admin.tenants.currency}: <span className="font-medium">{org.currency}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h3 className="font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium mb-3">
                        {pt.admin.tenants.stats}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <StatTile Icon={Users} label={pt.admin.tenants.stat_users} value={org.stats?.users} idx={0} />
                        <StatTile Icon={Briefcase} label={pt.admin.tenants.stat_customers} value={org.stats?.customers} idx={1} />
                        <StatTile Icon={FileText} label={pt.admin.tenants.stat_orders} value={org.stats?.service_orders} idx={2} />
                        <StatTile Icon={Headphones} label={pt.admin.tenants.stat_tickets} value={org.stats?.tickets} idx={3} />
                        <StatTile Icon={Plug} label={pt.admin.tenants.stat_connectors} value={org.stats?.connectors} idx={4} />
                    </div>
                </GlassCard>
            ))}
            </div>
        </div>
    );
};

export default TenantsPage;
